import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarDay,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaEdit,
  FaTrash
} from "react-icons/fa";
import { getSalesDaily, deleteSalesManual, getSalesTags, assignSalesPaymentTag } from "JS/API";
import { catchError } from "JS/common";
import CommonModal from "Components/CommonModal";
import ManualPaymentModal from "Components/ManualPaymentModal";
import SalesTagSelect from "Components/Sales/SalesTagSelect";
import unwrapResponse from "utils/unwrapResponse";

// 결제 유형별 설정
const PAYMENT_TYPE_CONFIG = {
  order: { color: '#1d4ed8', bg: '#dbeafe', label: '등록대행' },
  added: { color: '#c2410c', bg: '#ffedd5', label: '추가결제' },
  review: { color: '#15803d', bg: '#dcfce7', label: '검토요청' },
  manual: { color: '#f59e0b', bg: '#fef3c7', label: '수동' }
};

// 결제 상태별 설정
const PAID_STATUS_CONFIG = {
  paid: { color: '#166534', bg: '#dcfce7', label: '결제완료' },
  canceled: { color: '#991b1b', bg: '#fef2f2', label: '취소' },
  ready: { color: '#6b7280', bg: '#f3f4f6', label: '대기' },
  processing: { color: '#0e7490', bg: '#cffafe', label: '처리중' },
  failed: { color: '#be123c', bg: '#ffe4e6', label: '실패' },
  refund_request: { color: '#c2410c', bg: '#ffedd5', label: '환불요청' },
  refund: { color: '#6d28d9', bg: '#ede9fe', label: '환불완료' },
  partial_refund: { color: '#9333ea', bg: '#f3e8ff', label: '부분 환불' },
  manual: { color: '#f59e0b', bg: '#fef3c7', label: '수동등록' }
};

// 결제 수단 매핑
const PAYMENT_METHOD_MAP = {
  card: '카드',
  bank: '계좌이체',
  toss: '토스결제'
};

const ITEMS_PER_PAGE = 20;

// 날짜 포맷 헬퍼
const formatDate = (dateString) => {
  const [year, month, day] = dateString.split('-');
  return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`;
};

// 시간 포맷 헬퍼
const formatTime = (dateTimeString) => {
  if (!dateTimeString) return "-";
  const date = new Date(dateTimeString);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

// 스타일 정의 (컴포넌트 외부로 이동)
const styles = {
    panel: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      marginTop: '20px',
      overflow: 'hidden'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '20px 24px',
      borderBottom: '1px solid #e2e8f0',
      backgroundColor: '#f8fafc'
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0
    },
    summary: {
      display: 'flex',
      gap: '24px',
      padding: '16px 24px',
      backgroundColor: '#f1f5f9',
      borderBottom: '1px solid #e2e8f0',
      flexWrap: 'wrap'
    },
    summaryItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    summaryLabel: {
      fontSize: '13px',
      color: '#64748b'
    },
    summaryValue: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#0f172a'
    },
    content: {
      flex: 1,
      padding: '0',
      overflowX: 'auto'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: '14px 16px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '600',
      color: '#475569',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      backgroundColor: '#f8fafc',
      borderBottom: '2px solid #e2e8f0',
      position: 'sticky',
      top: 0
    },
    td: {
      padding: '14px 16px',
      fontSize: '14px',
      color: '#1e293b',
      borderBottom: '1px solid #f1f5f9'
    },
    row: {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600'
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 24px',
      color: '#64748b'
    },
    pagination: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      padding: '16px 24px',
      borderTop: '1px solid #e2e8f0',
      backgroundColor: '#f8fafc'
    },
    pageButton: {
      padding: '8px 14px',
      backgroundColor: '#ffffff',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '13px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.2s ease'
    },
    pageButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    pageInfo: {
      fontSize: '13px',
      color: '#64748b'
    },
    loadingOverlay: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '60px',
      color: '#64748b'
    },
  tossInfo: {
    fontSize: '11px',
    color: '#64748b',
    marginTop: '2px'
  },
  subText: {
    fontSize: '12px',
    color: '#64748b'
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  actionButtons: {
    display: 'flex',
    gap: '6px',
    whiteSpace: 'nowrap'
  },
  actionButton: {
    padding: '6px 10px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'background-color 0.2s ease'
  },
  editButton: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8'
  },
  deleteButton: {
    backgroundColor: '#fef2f2',
    color: '#dc2626'
  },
  badgeRow: {
    display: 'flex',
    gap: '4px',
    marginTop: '2px'
  },
  channelBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 6px',
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#6b7280'
  },
  serviceBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 6px',
    backgroundColor: '#f5f3ff',
    border: '1px solid #ddd6fe',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#6d28d9'
  }
};

// 호버 핸들러
const handleRowHover = (e, isEnter) => {
  e.currentTarget.style.backgroundColor = isEnter ? '#f8fafc' : 'transparent';
};

const SERVICE_TYPE_LABEL = {
  patent: '특허',
  design: '디자인',
  other: '기타'
};

const SalesDailyPanel = ({ date, service = "all", onRefreshCalendar }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [tags, setTags] = useState([]);

  // 수동 결제 모달 상태
  const [showManualModal, setShowManualModal] = useState(false);
  const [editManualId, setEditManualId] = useState(null);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  // 데이터 로드
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getSalesDaily(date, page, ITEMS_PER_PAGE, service);
      if (typeof result !== "object") {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      setData(result.data);
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [date, page, service, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadTags = useCallback(async () => {
    const result = await getSalesTags();
    if (typeof result === "object" && result?.data) {
      setTags(unwrapResponse(result)?.items || []);
    }
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const handleTagChange = useCallback(async (item, tagId) => {
    try {
      const result = await assignSalesPaymentTag({
        payment_type: item.payment_type,
        payment_pk: item.payment_pk,
        tag_id: tagId,
      });
      if (typeof result !== "object") {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "string") window.alert(detail);
      else catchError(error, navigate, setAlertBox, setAlert);
    }
  }, [loadData, navigate]);

  // 상세 페이지 이동
  const handleRowClick = useCallback((item) => {
    // 수동 결제는 클릭해도 페이지 이동하지 않음
    if (item.is_manual) return;

    if (item.payment_type === 'order' || item.payment_type === 'added') {
      if (item.apply_agent_pk) {
        navigate(`/agencies/${item.apply_agent_pk}`);
      }
    } else if (item.payment_type === 'review') {
      if (item.review_request_id) {
        navigate(`/review-self/${item.review_request_id}`);
      }
    }
  }, [navigate]);

  // 수동 등록 버튼 클릭
  const handleAddManual = useCallback(() => {
    setEditManualId(null);
    setShowManualModal(true);
  }, []);

  // 수동 결제 수정
  const handleEditManual = useCallback((e, manualId) => {
    e.stopPropagation();
    setEditManualId(manualId);
    setShowManualModal(true);
  }, []);

  // 수동 결제 삭제
  const handleDeleteManual = useCallback(async (e, manualId) => {
    e.stopPropagation();
    if (!window.confirm("수동 결제 내역을 삭제하시겠습니까?")) return;

    try {
      const result = await deleteSalesManual(manualId);
      if (typeof result !== "object") {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      // 삭제 후 데이터 새로고침
      loadData();
      onRefreshCalendar?.();
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    }
  }, [loadData, navigate, onRefreshCalendar]);

  // 수동 결제 모달 닫기
  const handleCloseManualModal = useCallback(() => {
    setShowManualModal(false);
    setEditManualId(null);
  }, []);

  // 수동 결제 등록/수정 성공
  const handleManualSuccess = useCallback(() => {
    loadData();
    onRefreshCalendar?.();
  }, [loadData, onRefreshCalendar]);

  // 페이지 변경 핸들러
  const handlePrevPage = useCallback(() => setPage(prev => prev - 1), []);
  const handleNextPage = useCallback(() => setPage(prev => prev + 1), []);

  // 알림 모달 확인 핸들러
  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  // 결제 수단 표시
  const getPaymentMethodLabel = (method) => PAYMENT_METHOD_MAP[method] || method || '-';

  const totalPages = data?.pagination?.total_pages || 1;
  const summary = data?.summary;

  return (
    <>
      <div style={styles.panel} data-testid="daily-panel">
        <div>
          {/* 헤더 */}
          <div style={styles.header}>
            <h2 style={styles.headerTitle}>
              <FaCalendarDay />
              {formatDate(date)} 매출 상세
            </h2>
            <div style={styles.headerButtons}>
              <button
                style={styles.addButton}
                onClick={handleAddManual}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                <FaPlus size={12} />
                수동 등록
              </button>
            </div>
          </div>

          {/* 요약 */}
          {data && !isLoading && (
            <div style={styles.summary}>
              <div style={styles.summaryItem}>
                <div>
                  <span style={styles.summaryLabel}>총 매출:</span>
                  <span style={{ ...styles.summaryValue, color: '#1f2937' }}>
                    {summary?.total_amount?.toLocaleString() || 0}원
                  </span>
                </div>
                {summary?.total_refund_amount > 0 && (
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                    환불 -{summary.total_refund_amount.toLocaleString()}원
                    <span style={{ color: '#1d4ed8', marginLeft: '6px' }}>
                      실 매출 {((summary?.total_amount || 0) - (summary?.total_refund_amount || 0)).toLocaleString()}원
                    </span>
                  </div>
                )}
                {summary?.net_revenue > 0 && (
                  <div style={{ fontSize: '11px', color: '#059669', marginTop: '2px' }}>
                    순매출 {summary.net_revenue.toLocaleString()}원
                    <span style={{ color: '#9ca3af', marginLeft: '4px' }}>(VAT,관납료 제외)</span>
                  </div>
                )}
                {summary?.by_status?.partial_refund?.amount > 0 && (
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                    ※ 부분 환불 {summary.by_status.partial_refund.amount.toLocaleString()}원 순매출 미차감 (항목 구분 불가)
                  </div>
                )}
              </div>
              {summary?.by_status?.paid?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>결제완료:</span>
                  <span style={{ ...styles.summaryValue, color: '#166534' }}>
                    {summary.by_status.paid.count}건
                  </span>
                </div>
              )}
              {summary?.by_status?.canceled?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>취소:</span>
                  <span style={{ ...styles.summaryValue, color: '#991b1b' }}>
                    {summary.by_status.canceled.count}건
                  </span>
                </div>
              )}
              {summary?.by_status?.refund?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>환불완료:</span>
                  <span style={{ ...styles.summaryValue, color: '#6d28d9' }}>
                    {summary.by_status.refund.count}건
                    <span style={{ fontSize: '12px', fontWeight: '500', marginLeft: '4px' }}>
                      ({summary.by_status.refund.amount?.toLocaleString()}원)
                    </span>
                  </span>
                </div>
              )}
              {summary?.by_status?.partial_refund?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>부분 환불:</span>
                  <span style={{ ...styles.summaryValue, color: '#9333ea' }}>
                    {summary.by_status.partial_refund.count}건
                    <span style={{ fontSize: '12px', fontWeight: '500', marginLeft: '4px' }}>
                      ({summary.by_status.partial_refund.amount?.toLocaleString()}원)
                    </span>
                  </span>
                </div>
              )}
              {summary?.by_status?.refund_request?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>환불요청:</span>
                  <span style={{ ...styles.summaryValue, color: '#c2410c' }}>
                    {summary.by_status.refund_request.count}건
                  </span>
                </div>
              )}
              {summary?.by_status?.ready?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>대기:</span>
                  <span style={{ ...styles.summaryValue, color: '#6b7280' }}>
                    {summary.by_status.ready.count}건
                  </span>
                </div>
              )}
              {summary?.by_status?.processing?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>처리중:</span>
                  <span style={{ ...styles.summaryValue, color: '#0e7490' }}>
                    {summary.by_status.processing.count}건
                  </span>
                </div>
              )}
              {summary?.by_status?.failed?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>실패:</span>
                  <span style={{ ...styles.summaryValue, color: '#be123c' }}>
                    {summary.by_status.failed.count}건
                  </span>
                </div>
              )}
              {summary?.by_status?.manual?.count > 0 && (
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>수동:</span>
                  <span style={{ ...styles.summaryValue, color: '#f59e0b' }}>
                    {summary.by_status.manual.count}건
                  </span>
                </div>
              )}
            </div>
          )}

          {/* 콘텐츠 */}
          <div style={styles.content}>
            {isLoading ? (
              <div style={styles.loadingOverlay}>데이터 로딩 중...</div>
            ) : !data?.items?.length ? (
              <div style={styles.emptyState}>
                <p>해당 날짜에 결제 내역이 없습니다.</p>
              </div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>유형</th>
                    <th style={styles.th}>고객명</th>
                    <th style={styles.th}>금액</th>
                    <th style={styles.th}>결제수단</th>
                    <th style={styles.th}>태그</th>
                    <th style={styles.th}>시간</th>
                    <th style={styles.th}>상태</th>
                    <th style={styles.th}>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, idx) => {
                    const typeConfig = PAYMENT_TYPE_CONFIG[item.payment_type] || PAYMENT_TYPE_CONFIG.order;
                    const statusConfig = PAID_STATUS_CONFIG[item.paid_status] || PAID_STATUS_CONFIG.paid;
                    const isManual = item.is_manual;

                    return (
                      <tr
                        key={`${item.payment_type}-${item.payment_pk}-${idx}`}
                        style={{
                          ...styles.row,
                          cursor: isManual ? 'default' : 'pointer'
                        }}
                        onClick={() => handleRowClick(item)}
                        onMouseEnter={(e) => handleRowHover(e, true)}
                        onMouseLeave={(e) => handleRowHover(e, false)}
                      >
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.badge,
                              backgroundColor: typeConfig.bg,
                              color: typeConfig.color
                            }}
                          >
                            {typeConfig.label}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div>{item.user_name || '-'}</div>
                          {!isManual && <div style={styles.subText}>{item.user_id}</div>}
                          {((isManual && item.channel) || SERVICE_TYPE_LABEL[item.service_type]) && (
                            <div style={styles.badgeRow}>
                              {isManual && item.channel && (
                                <span style={styles.channelBadge}>
                                  {item.channel === 'kmong' ? '크몽' :
                                   item.channel === 'bank_transfer' ? '통장입금' : item.channel}
                                </span>
                              )}
                              {SERVICE_TYPE_LABEL[item.service_type] && (
                                <span style={styles.serviceBadge}>{SERVICE_TYPE_LABEL[item.service_type]}</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: '600' }}>
                            {item.merchant_price?.toLocaleString() || 0}원
                          </div>
                          {item.net_revenue > 0 && (
                            <div style={{ fontSize: '11px', color: '#059669', marginTop: '2px' }}>
                              순매출 {item.net_revenue.toLocaleString()}원
                            </div>
                          )}
                          {(item.paid_status === 'partial_refund' || item.paid_status === 'refund') && (
                            <div style={{ fontSize: '11px', marginTop: '2px' }}>
                              <span style={{ color: '#dc2626' }}>환불 {item.refund_amount?.toLocaleString() || 0}원</span>
                              {item.paid_status === 'partial_refund' && (
                                <span style={{ color: '#2563eb', marginLeft: '6px' }}>잔액 {item.balance_amount?.toLocaleString() || 0}원</span>
                              )}
                            </div>
                          )}
                          {item.paid_reason && (
                            <div style={styles.subText}>{item.paid_reason}</div>
                          )}
                          {isManual && item.description && (
                            <div style={styles.subText}>{item.description}</div>
                          )}
                        </td>
                        <td style={styles.td}>
                          {isManual ? (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          ) : (
                            <>
                              <div>{getPaymentMethodLabel(item.paid_method)}</div>
                              {item.toss && (
                                <div style={styles.tossInfo}>
                                  {item.toss.payment_provider}
                                  {item.toss.payment_number && ` (${item.toss.payment_number})`}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                          <SalesTagSelect
                            tags={tags}
                            value={item.tag?.id}
                            onChange={(tagId) => handleTagChange(item, tagId)}
                            ariaLabel={`${item.user_name || item.payment_pk} 태그`}
                          />
                        </td>
                        <td style={styles.td}>
                          {isManual ? '-' : formatTime(item.paid_at)}
                        </td>
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.badge,
                              backgroundColor: statusConfig.bg,
                              color: statusConfig.color,
                              ...(item.paid_status === 'canceled' ? { textDecoration: 'line-through' } : {})
                            }}
                          >
                            {statusConfig.label}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {isManual ? (
                            <div style={styles.actionButtons}>
                              <button
                                style={{ ...styles.actionButton, ...styles.editButton }}
                                onClick={(e) => handleEditManual(e, item.manual_id)}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#bfdbfe'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                              >
                                <FaEdit size={11} />
                                수정
                              </button>
                              <button
                                style={{ ...styles.actionButton, ...styles.deleteButton }}
                                onClick={(e) => handleDeleteManual(e, item.manual_id)}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                              >
                                <FaTrash size={11} />
                                삭제
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* 페이지네이션 */}
          {data && totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={{
                  ...styles.pageButton,
                  ...(page === 1 ? styles.pageButtonDisabled : {})
                }}
                onClick={handlePrevPage}
                disabled={page === 1}
              >
                <FaChevronLeft size={12} />
                이전
              </button>
              <span style={styles.pageInfo}>
                {page} / {totalPages} 페이지
              </span>
              <button
                style={{
                  ...styles.pageButton,
                  ...(page === totalPages ? styles.pageButtonDisabled : {})
                }}
                onClick={handleNextPage}
                disabled={page === totalPages}
              >
                다음
                <FaChevronRight size={12} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 공통 알림 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={handleAlertOk}
        />
      )}

      {/* 수동 결제 등록/수정 모달 */}
      {showManualModal && (
        <ManualPaymentModal
          date={date}
          manualId={editManualId}
          onClose={handleCloseManualModal}
          onSuccess={handleManualSuccess}
        />
      )}
    </>
  );
};

export default SalesDailyPanel;
